import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { AnimatedSection } from '@/components/shared/AnimatedSection'
import { ParallaxImage } from '@/components/shared/ParallaxImage'
import { Button } from '@/components/ui/button'
import { ROOMS_ROUTE, SITE_NAME } from '@/lib/constants'
import { getImageUrl } from '@/lib/image-url'

function ImageTile({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="relative aspect-[3/4] overflow-hidden rounded-2xl">
      <img
        src={getImageUrl(src)}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
        loading="lazy"
      />
    </div>
  )
}

function Block({
  title,
  text,
  images,
  delay = 0,
}: {
  title: string
  text: string
  images: { src: string; alt: string }[]
  delay?: number
}) {
  return (
    <AnimatedSection delay={delay}>
      <div className="mb-20">
        <h2 className="mb-4 font-heading text-3xl font-bold text-wood-dark">{title}</h2>
        <p className="mb-8 max-w-3xl text-lg leading-relaxed text-stone">{text}</p>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {images.map((img) => (
            <ImageTile key={img.src} src={img.src} alt={img.alt} />
          ))}
        </div>
      </div>
    </AnimatedSection>
  )
}

export function ComayaguaPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language.startsWith('en') ? 'en' : 'es'
  const es = locale === 'es'

  return (
    <>
      <Helmet>
        <title>{`${t('comayagua.title')} | ${SITE_NAME}`}</title>
        <meta name="description" content={t('comayagua.subtitle') as string} />
      </Helmet>

      <div data-hero className="relative h-[70vh] min-h-[500px]">
        <ParallaxImage
          src={getImageUrl('/images/comayagua/cathedral-pigeons.webp')}
          alt={t('comayagua.title')}
          className="h-full w-full"
          overlay
          overlayOpacity={0.5}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="mx-auto max-w-3xl px-4 text-center text-white">
            <h1 className="font-heading text-4xl font-bold md:text-5xl lg:text-6xl">
              {t('comayagua.title')}
            </h1>
            <p className="mt-2 text-lg text-gold md:text-xl">{t('comayagua.subtitle')}</p>
            <p className="mt-6 text-lg text-white/90">{t('comayagua.hero_text')}</p>
          </div>
        </div>
      </div>

      <section className="bg-cream-light py-20">
        <div className="mx-auto max-w-7xl px-4">
          <Block
            title={t('comayagua.history_title')}
            text={t('comayagua.history_text')}
            images={[
              { src: '/images/comayagua/history-bust-statue.webp', alt: 'Busto histórico' },
              { src: '/images/comayagua/history-colonial-carriage.webp', alt: 'Carruaje colonial' },
              { src: '/images/comayagua/history-yellow-arch.webp', alt: 'Arco amarillo' },
              { src: '/images/comayagua/history-cathedral-bell.webp', alt: 'Campana de la Catedral' },
            ]}
          />
          <Block
            delay={0.1}
            title={t('comayagua.cathedral_title')}
            text={t('comayagua.cathedral_text')}
            images={[
              { src: '/images/comayagua/cathedral-facade-tourists.webp', alt: 'Fachada barroca' },
              { src: '/images/comayagua/cathedral-fountain-girl.webp', alt: 'Fuente del Parque Central' },
              { src: '/images/comayagua/cathedral-golden-altar-full.webp', alt: 'Altar dorado' },
              { src: '/images/comayagua/cathedral-pipe-organ.webp', alt: 'Órgano de tubos' },
            ]}
          />
          <Block
            delay={0.1}
            title={t('comayagua.architecture_title')}
            text={t('comayagua.architecture_text')}
            images={[
              { src: '/images/comayagua/plaza-tourists-tower.webp', alt: 'Plaza con torre colonial' },
              { src: '/images/comayagua/street-mural.webp', alt: 'Mural histórico' },
              { src: '/images/comayagua/colonial-colorful-street.webp', alt: 'Calle colonial' },
              { src: '/images/comayagua/girls-walking-street.webp', alt: 'Centro histórico' },
            ]}
          />
          <Block
            delay={0.1}
            title={t('comayagua.museums_title')}
            text={t('comayagua.museums_text')}
            images={[
              { src: '/images/comayagua/museum-masks.webp', alt: 'Máscaras tradicionales' },
              { src: '/images/comayagua/museum-religious-gold.webp', alt: 'Arte religioso colonial' },
              { src: '/images/comayagua/museum-angels.webp', alt: 'Ángeles dorados' },
              { src: '/images/comayagua/museum-virgin-painting.webp', alt: 'Pintura colonial' },
            ]}
          />
          <Block
            delay={0.1}
            title={t('comayagua.traditions_title')}
            text={t('comayagua.traditions_text')}
            images={[
              { src: '/images/comayagua/traditions-masks.webp', alt: 'Máscaras' },
              { src: '/images/comayagua/traditions-procession-virgin.webp', alt: 'Procesión' },
              { src: '/images/comayagua/traditions-penitents.webp', alt: 'Penitentes' },
              { src: '/images/comayagua/traditions-sawdust-carpet-colorful.webp', alt: 'Alfombra de aserrín' },
            ]}
          />

          <AnimatedSection delay={0.2}>
            <div className="rounded-2xl bg-gradient-to-br from-terracotta to-wood-dark p-12 text-center text-white">
              <h2 className="mb-4 font-heading text-3xl font-bold md:text-4xl">
                {es
                  ? '¿Qué esperás para conocer Comayagua?'
                  : 'What are you waiting for to discover Comayagua?'}
              </h2>
              <p className="mx-auto mb-8 max-w-2xl text-lg text-white/90">
                {es
                  ? 'Reservá tu estancia en Hotel Nueva Valladolid y viví tu propia experiencia en el corazón colonial de Honduras. La historia, la cultura y la calidez te esperan.'
                  : 'Book your stay at Hotel Nueva Valladolid and live your own experience in the colonial heart of Honduras. History, culture, and warmth await you.'}
              </p>
              <Link to={ROOMS_ROUTE}>
                <Button
                  size="lg"
                  className="min-w-[200px] bg-gold text-wood-dark hover:bg-gold-light text-base"
                >
                  {es ? 'Reservar Ahora' : 'Book Now'}
                </Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  )
}
